class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });
  }

  handleMegaItemActive(dropdown) {
    // Calculate backdrop height.
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme);
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this.onScrollHandler = this._onScroll.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.stickyClasses.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.header.querySelector("site-nav");
    this.siteNav && this.siteNav.closeMegaDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
      }
    }
  }
}
customElements.define("header-menu", HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.megaItems = this.querySelectorAll(".f-site-nav__item--mega");
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");
    if (this.isHover) {
      this.megaItems &&
        this.megaItems.forEach((megaItem, index) => {
          megaItem.addEventListener("mouseenter", (evt) =>
            this.onMenuItemEnter(evt, index)
          );
          megaItem.addEventListener("mouseleave", (evt) =>
            this.onMenuItemLeave(evt, index)
          );
        });
    }
  }

  onMenuItemEnter(evt) {
    clearTimeout(this.timeoutLeave);

    const { target } = evt;

    /**
     * If hover from prev item to sibling items then need to close prev sub menu immediately.
     */
    if (!target.classList.contains(this.classes.itemActive)) {
      this.megaItems &&
        this.megaItems.forEach((megaItem) => {
          megaItem.classList.remove(this.classes.itemActive);
        });
    }

    const dropdown = target.querySelector(".f-site-nav__dropdown");

    if (dropdown) {
      this.header.handleMegaItemActive(dropdown);

      this.timeoutEnter = setTimeout(() => {
        target.classList.add(this.classes.itemActive);
      });
    }
  }

  onMenuItemLeave(evt) {
    const { target } = evt;

    clearTimeout(this.timeoutEnter);
    
    this.header.handleMegaItemDeactive();

    this.timeoutLeave = setTimeout(() => {
      target.classList.remove(this.classes.itemActive);
    });
  }

  closeMegaDropdowns() {
    clearTimeout(this.timeoutEnter);
    this.megaItems &&
      this.megaItems.forEach((megaItem) => {
        megaItem.classList.remove(this.classes.itemActive);
      });
    this.timeoutLeave = setTimeout(() => {
      this.header.handleMegaItemDeactive();
    });
  }

  disconnectedCallback() {
    this.megaItems &&
      this.megaItems.forEach((megaItem, index) => {
        megaItem.removeEventListener("mouseenter", (evt) =>
          this.onMenuItemEnter(evt, index)
        );
        megaItem.removeEventListener("mouseleave", (evt) =>
          this.onMenuItemLeave(evt, index)
        );
      });
  }
}
customElements.define("site-nav", SiteNav);

// Hoverd JS
// document.addEventListener("DOMContentLoaded", () => {
//   const items = document.querySelectorAll(".f-site-nav__itemf");
//   const nav = document.querySelector(".f-site-nav");
//   if (!items.length || !nav) return;
//   const first = items[0];
//   const setActive = (item) => {
//     items.forEach((i) => i.classList.remove("is-hovered", "active"));
//     item.classList.add("is-hovered", "active");
//   };
//   setActive(first);
//   items.forEach((item, index) => {
//     item.addEventListener("mouseenter", () => {
//       setActive(item);
//     });
//     item.addEventListener("mouseleave", () => {
//       if (index === 0) return;
//       setActive(first);
//     });
//   });
//   nav.addEventListener("mouseleave", () => {
//     setActive(first);
//   });
// });


// document.addEventListener("DOMContentLoaded", () => {
//   const items = document.querySelectorAll(".f-site-nav__itemf");
//   const nav = document.querySelector(".f-site-nav");
//   if (!items.length || !nav) return;
//   const setActive = (item) => {
//     items.forEach((i) => i.classList.remove("is-hovered", "active"));
//     item.classList.add("is-hovered", "active");
//   };
//   setActive(items[0]);

//   items.forEach((item) => {
//     item.addEventListener("mouseenter", () => {
//       setActive(item);
//     });
//   });
// });
document.addEventListener("DOMContentLoaded", () => {
  const items = document.querySelectorAll(".f-site-nav__itemf");
  const navContainers = document.querySelectorAll(".f-site-nav");

  if (!items.length || !navContainers.length) return;

  const setActive = (item) => {
    items.forEach((i) => i.classList.remove("is-hovered", "active"));
    if (item) {
      item.classList.add("is-hovered", "active");
      console.log("Activated:", item.querySelector("a")?.textContent?.trim());
    }
  };
  const normalizePath = (path) =>
    path.toLowerCase().replace(/\/+$/, "");

  const currentPath = normalizePath(window.location.pathname);
  console.log("Current Path:", currentPath);

  const getLinkPath = (link) => {
    if (!link) return null;
    try {
      return normalizePath(
        new URL(link.href, window.location.origin).pathname
      );
    } catch (e) {
      return null;
    }
  };

  const isLinkActive = (link) => {
    const rawHref = link.getAttribute("href");
    if (!rawHref || rawHref === "#" || rawHref.startsWith("javascript:")) {
      return false; 
    }
    const linkPath = getLinkPath(link);
    if (!linkPath) return false;
    if (linkPath === "") return currentPath === ""; 
    return (
      currentPath === linkPath ||
      currentPath.startsWith(linkPath + "/")
    );
  };

  let activeParentId = null;
  let bestMatchLength = -1; 


  const allLinks = document.querySelectorAll(".f-site-nav a");

  for (const link of allLinks) {
    if (!isLinkActive(link)) continue;

    const linkPath = getLinkPath(link);
    console.log("Candidate link:", link.href, "| text:", link.textContent?.trim());

    let candidateParentId = null;

    const level3Parent = link.closest(".scmml3");
    const level2Parent = link.closest(".sc-site-nav-l-2");
    const level1Parent = link.closest(".f-site-nav__itemf");

    if (level3Parent) {
      const parentItem = link.closest(".f-site-nav__item");
      if (parentItem && parentItem.dataset.parentId) {
        candidateParentId = parentItem.dataset.parentId;
      }
    } else if (level2Parent) {
      const parentItem = link.closest(".f-site-nav__item");
      if (parentItem && parentItem.dataset.parentId) {
        candidateParentId = parentItem.dataset.parentId;
      }
    } else if (level1Parent) {
      candidateParentId =
        level1Parent.dataset.parentId || level1Parent.dataset.parentHandle;
    }

    if (!candidateParentId) {
      const anyParent = link.closest("[data-parent-id]");
      if (anyParent) candidateParentId = anyParent.dataset.parentId;
    }

    if (candidateParentId && linkPath.length > bestMatchLength) {
      activeParentId = candidateParentId;
      bestMatchLength = linkPath.length;
      console.log("New best parent ID:", activeParentId, "(path length:", linkPath.length, ")");
    }
  }

  const HOME_PARENT_ID = "women";
  const isHomePage = currentPath === "" || currentPath === "/index";

  if (isHomePage) {
    activeParentId = HOME_PARENT_ID;
  }

  if (!activeParentId) {
    console.log("No direct match found, checking segment matches...");

    const pathSegments = currentPath.split("/").filter(Boolean);

    for (const item of items) {
      const parentId = item.dataset.parentId || item.dataset.parentHandle;
      if (parentId && pathSegments.includes(parentId.toLowerCase())) {
        activeParentId = parentId;
        console.log("Found parent ID from segment match:", activeParentId);
        break;
      }
    }
  }

  let activeItem = null;

  if (activeParentId) {
    console.log("Looking for parent with ID:", activeParentId);

    for (const item of items) {
      const itemParentId = item.dataset.parentId || item.dataset.parentHandle;
      console.log(
        "  - Checking:",
        item.querySelector("a")?.textContent?.trim(),
        "ID:",
        itemParentId
      );

      if (itemParentId === activeParentId) {
        activeItem = item;
        console.log("Found matching parent!");
        break;
      }
    }
  }
  if (!activeItem && isHomePage) {
    for (const item of items) {
      const text = item.querySelector("a")?.textContent?.trim().toLowerCase();
      if (text === "women") {
        activeItem = item;
        activeParentId = item.dataset.parentId || item.dataset.parentHandle || "women";
        break;
      }
    }
  }
  if (activeItem) {
    setActive(activeItem);

    document.querySelectorAll(".sc-site-nav-l-2").forEach((nav) => {
      nav.classList.toggle("is-open", nav.dataset.parentId === activeParentId);
    });
  } else {
    console.log("No match found - clearing all");
    items.forEach((i) => i.classList.remove("is-hovered", "active"));
  }
  items.forEach((item) => {
    item.addEventListener("mouseenter", () => {
      setActive(item);
    });
  });
});
// Nav Drawer
 (function () {
        const lv1 = document.querySelectorAll('.lv_o');
        const lv2 = document.querySelectorAll('.child_wrapper');
        const gcPanel = document.getElementById('gc-panel');
        const gcTitle = document.getElementById('gc-title');

        function showL1(idx) {
          lv1.forEach(el => el.classList.toggle('active', +el.dataset.i === idx));
          lv2.forEach(ul => ul.classList.toggle('active', +ul.dataset.i === idx));
          gcPanel.classList.remove('open');
        }

        document.querySelector('.drawer-menu_header').addEventListener('click', e => {
          const li = e.target.closest('.lv_o');
          if (li) showL1(+li.dataset.i);
        });

        document.getElementById('lv2-wrap').addEventListener('click', e => {
          const li = e.target.closest('.has-children');
          if (!li) return;
          const { pi, ci } = li.dataset;
          document.querySelectorAll('.grandchild_wrapper').forEach(ul => ul.classList.remove('active'));
          const target = document.querySelector(`.grandchild_wrapper[data-pi="${pi}"][data-ci="${ci}"]`);
          if (target) target.classList.add('active');
          gcTitle.textContent = li.querySelector('a').textContent.trim();
          gcPanel.classList.add('open');
        });

        document.getElementById('gc-back').addEventListener('click', () => gcPanel.classList.remove('open'));

        showL1(0);
      })();
